import { useEffect, useState } from "react";
import { UserSummary } from "../../../types/User";
import { PagedResults } from "../../../types/PagedResults";
import { useNavigate, useSearchParams } from "react-router-dom";
import useUserService from "../../../services/UserService";
import PageTitle from "../../../components/PageTitle";
import Ghost from "../../../components/Ghost";
import ArrowUpwardTwoToneIcon from "@mui/icons-material/ArrowUpwardTwoTone";
import ArrowDownwardTwoToneIcon from "@mui/icons-material/ArrowDownwardTwoTone";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import PositiveButton from "../../../components/PositiveButton";
import useAnalyticsService from "../../../services/AnalyticsService";
import { ExperienceAnalyticsSummary } from "../../../types/Analytics";
import ExperienceAnalyticsBlock from "../../../components/Analytics/ExperienceAnalyticsBlock";
import AdminAnalyticsSummary from "../../../components/Analytics/AdminAnalyticsSummary";

export default function AdminExperienceAnalyticsSummaryPage() {
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] =
    useState<PagedResults<ExperienceAnalyticsSummary> | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Initialize formData with search params from URL if available, else use defaults
  const [formData, setFormData] = useState({
    searchValue: searchParams.get("searchValue") || "",
    page: parseInt(searchParams.get("page") || "1"),
    orderBy: searchParams.get("orderBy") || "count",
    orderDirection: searchParams.get("orderDirection") || "desc",
  });

  const { searchExperienceData } = useAnalyticsService();
  const debounceDelay = 500;

  const search = () => {
    setIsSearching(true);

    // Update the URL with current search parameters
    setSearchParams({
      searchValue: formData.searchValue,
      page: formData.page.toString(),
      orderBy: formData.orderBy,
      orderDirection: formData.orderDirection,
    });

    searchExperienceData(
      formData.searchValue,
      formData.page,
      formData.orderBy,
      formData.orderDirection,
      9
    )
      .then((searchResults) => {
        setResults(searchResults);
      })
      .catch((error) => {
        console.error("Error searching analytics:", error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  const handleInputChange = (e: any) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      searchValue: value,
      page: 1, // Reset to the first page when searching
    });
  };

  const handlePageChange = (newPage: number) => {
    setFormData((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleSort = (column: string) => {
    setFormData((prev) => ({
      ...prev,
      orderBy: column,
      orderDirection:
        prev.orderBy === column && prev.orderDirection === "asc"
          ? "desc"
          : "asc", // Toggle direction
      page: 1, // Reset to first page on sort
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      search();
    }, debounceDelay);

    return () => {
      clearTimeout(handler);
    };
  }, [
    formData.searchValue,
    formData.page,
    formData.orderBy,
    formData.orderDirection,
  ]); // Watch for changes to formData

  useEffect(() => {
    search(); // Perform an initial search based on URL params when the component mounts
  }, []);

  return (
    <div className="flex flex-col gap-4 py-4">

      <section role="heading" className="grid grid-cols-1">
        <h2 className="text-2xl">Aggregate summary</h2>
        <p>This provides a sum of all experience data aggregated.</p>
        <AdminAnalyticsSummary />
      </section>

      <hr className="border-cyan-900" />
      
      <section role="heading" className="grid grid-cols-1 xl:grid-cols-2">
        <div className="grow">
          <h2 className="text-2xl grow">Experience analytics</h2>
          <p>A quick look at the numbers by experience:</p>
        </div>
        <div role="searchbox" className="flex flex-row gap-2 items-center">
          <label
            htmlFor="search"
            className="hidden xl:w-auto xl:block leading-6"
          >
            Filter:
          </label>
          <div className="mt-2 w-full">
            <input
              type="text"
              name="searchValue"
              placeholder="Search term"
              value={formData.searchValue}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded text-black"
            />
          </div>
        </div>
      </section>

      <section role="content">
        {isSearching && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 9 }, (_, i) => (
              <div className="border-t border-cyan-900" key={i}>
                <div className="text-left p-4">
                  <Ghost additionalClasses="p-4 rounded min-h-80" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isSearching && results && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {!results.items || results.items.length === 0 ? (
              <div className="border-t border-cyan-900">
                <div className="text-left p-4">No results found.</div>
              </div>
            ) : (
              results.items.map((summary) => (
                <ExperienceAnalyticsBlock
                  key={summary.experienceId}
                  experience={summary}
                />
              ))
            )}
          </div>
        )}
      </section>

      {/* Pagination Section */}
      {results && results.totalPages > 1 && (
        <section className="flex justify-between items-center mt-4">
          <PositiveButton
            onClick={() => handlePageChange(formData.page - 1)}
            disabled={formData.page === 1}
          >
            Previous
          </PositiveButton>
          <span>
            Page {formData.page} of {results.totalPages}
          </span>
          <PositiveButton
            onClick={() => handlePageChange(formData.page + 1)}
            disabled={formData.page === results.totalPages}
          >
            Next
          </PositiveButton>
        </section>
      )}
    </div>
  );
}
